import React, { useEffect, useMemo, useState } from 'react';
import { Input, Badge, Collapse } from 'antd';
import { Button } from '../../../../components/Button/Button';
import { ScaleLoader } from 'react-spinners';
import { useTheme } from 'styled-components';
import { Api } from '../../../Api/Api';
import { IconButton } from '../../../../components/IconButton/IconButton';
import { copyInsightsToClipboard } from './IExtendedClipboard';
import { getSeverityColor, IAiAnalysisResponse } from '../../../store/Insights/InsightsModels';
import { Panel } from './InsightsModal';

/**
 * Component for requesting and displaying AI analysis from a selected provider/model.
 * Allows user to choose from available AI providers and models, submit analysis requests,
 * and view results including overview and categorized concerns with severity levels.
 */
export const InsightReport = (props: { prompt: string; entityId: string; authToken: string; subject: string; persona: string; snapshotDate: string }) => 
{
	const { prompt, entityId, authToken, subject, persona, snapshotDate } = props;
	const api = useMemo(() => new Api('api/7', authToken, undefined), [authToken]);
	const theme = useTheme();
	const defaultProvider = 'CropwiseAi';
	const defaultModel = 'anthropic.claude-3-5-sonnet-20240620-v1:0';
	const [providers, setProviders] = useState<string[]>([defaultProvider]);
	const [selectedProvider, _setSelectedProvider] = useState<string>(defaultProvider);
	const [models, setModels] = useState<string[]>([defaultModel]);
	const [selectedModel, _setSelectedModel] = useState<string>(defaultModel);
	const [insight, setInsight] = useState<IAiAnalysisResponse>();
	const [isSubmittingInsights, setIsSubmittingInsights] = useState<boolean>(false);

	// Validates all requirements before allowing submission
	const canSubmit = (prompt ?? '').trim().length > 3 
		&& !isSubmittingInsights 
		&& selectedProvider 
		&& selectedModel 
		&& providers.length 
		&& models.length
		&& providers.includes(selectedProvider)
		&& models.includes(selectedModel);

	/**
	 * Updates provider selection and clears dependent state (models, selected model, results).
	 * Dependent state must be cleared because models are provider-specific.
	 */
	const setSelectedProvider = (provider: string) => 
	{
		_setSelectedProvider(provider);
		setModels([]);
		_setSelectedModel(undefined);
		setInsight(undefined);
	};

	/**
	 * Updates model selection and clears previous results.
	 * Results must be cleared because they are model-specific.
	 */
	const setSelectedModel = (model: string) => 
	{
		_setSelectedModel(model);
		setInsight(undefined);
	};

	// Fetch available AI providers on component mount
	useEffect(() => 
	{
		const fetchProviders = async () => 
		{
			setModels([]);
			try 
			{
				const response = await api.getAsync<string[]>('/ai/providers');
				setProviders(response.Data);
			}
			catch (error) 
			{
				console.error('Error fetching providers:', error);
			}
		};

		fetchProviders();
	}, [api]);

	// Fetch available models when provider changes
	useEffect(() => 
	{
		const fetchModels = async () => 
		{
			try 
			{
				setModels([]);
				const response = await api.getAsync<string[]>(`/ai/providers/${selectedProvider}/models`);
				setModels(response.Data);
				// Auto-select first model in the list
				setSelectedModel(response.Data[0]);
			}
			catch (error) 
			{
				console.error('Error fetching models:', error);
			}
		};

		if (selectedProvider) 
		{
			fetchModels();
		}
	}, [api, selectedProvider]);

	/**
	 * Submits the field analysis request to the AI API with selected provider/model.
	 * Clears previous results and displays loading state during the potentially long-running request.
	 */
	const handleInsightsSubmit = async () => 
	{
		setInsight(undefined);
		setIsSubmittingInsights(true);
		try 
		{
			const result = await api.postAsync<IAiAnalysisResponse>(`/ai/subjects/${subject}/personas/${persona}/entity/${entityId}/analysis`, {
				Prompt: prompt,
				Provider: selectedProvider,
				Model: selectedModel,
				Snapshot: snapshotDate
			});
			setInsight(result.Data);
		}
		catch (error) 
		{
			console.error('Error submitting insights:', error);
		}

		finally 
		{
			setIsSubmittingInsights(false);
		}
	};

	// Convert elapsed time from milliseconds to seconds for display
	const elapsed = Math.round(insight?.ElapsedMs / 1000);
	const cost = insight?.CostEstimate;
	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<select value={selectedProvider} onChange={(e) => setSelectedProvider(e.target.value)}>
				{providers.map((provider) => (
					<option key={provider} value={provider}>
						{provider}
					</option>
				))}
				{
					providers.length === 0 && <option disabled>Loading providers...</option>
				}
			</select>
			<select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} style={{ marginTop: '10px', marginBottom: '20px' }}>
				{models.map((model) => (
					<option key={model} value={model}>
						{model}
					</option>
				))}
				{
					models.length === 0 && <option disabled>Loading models...</option>
				}
			</select>
			<div style={{ marginBottom: '20px' }}>
				<Button
					variant='main'
					onClick={handleInsightsSubmit}
					disabled={!canSubmit}
					style={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					{isSubmittingInsights ? (
						<>
							<ScaleLoader color={theme.colors.white} loading={true} height={15} />
							<span style={{ marginLeft: '10px' }}>Submitting (may take up to 5 minutes)...</span>
						</>
					) : (
						'Submit'
					)}
				</Button>
			</div>
			{/* Display results once available */}
			{insight &&
				<>
					{/* Header with copy button, provider/model info, and metadata */}
					<label style={{
						display: 'flex',
						alignItems: 'center',
						flexDirection: 'row',
						marginBottom: '8px',
						fontWeight: theme.fontWeights.bold,
						fontSize: theme.fontSizes.normal
					}}>
						<IconButton
							onClick={() => copyInsightsToClipboard(insight.Analysis, `${selectedProvider}.${selectedModel}`, subject)}>
							📋
						</IconButton>
						<span>{insight.Provider} {insight.Model}</span>
						<span>{cost !== undefined ? ` (Cost Estimate: $${cost})` : ''}</span>
						<span>{elapsed ? ` (Response Time: ${elapsed}s)` : ''}</span>
					</label>
					<Input.TextArea
						value={insight?.Analysis.Overview || ''}
						readOnly
						placeholder="Results will appear here..."
						style={{
							backgroundColor: theme.colors.lightestGrey,
							resize: 'none'
						}}
						rows={6} />
					{/* Collapsible panels for each concern with severity badges */}
					<Collapse destroyInactivePanel={false}>
						{insight?.Analysis.Concerns.map((item, index) => 
						{
							return (
								<Panel
									key={index}
									header={<div style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: '100%',
										paddingRight: '8px'
									}}>
										<span>{item.Headline}</span>
										<Badge
											count={item.Severity.toUpperCase()}
											style={{
												backgroundColor: getSeverityColor(item.Severity),
												borderRadius: '4px',
												fontSize: '11px',
												fontWeight: theme.fontWeights.bold,
												padding: '0 8px',
												height: '20px',
												lineHeight: '20px'
											}} />
									</div>}
								>
									<p>{item.Details}</p>
									{item.Recommendation?.length && <p><strong>Recommendation:</strong> {item.Recommendation}</p>}
								</Panel>
							);
						})}
					</Collapse>
				</>}
		</div>
	);
};
